import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Headphones,
  Phone,
  PhoneCall,
  Plus,
  Shield,
  Star,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { ROUTES } from "@/constants/routes";
import { queryKeys } from "@/constants/queryKeys";
import { usePhoneStore } from "@/features/phone";
import { cn } from "@/lib/cn";
import { resolveMediaUrl } from "@/lib/media";
import { FloatingDialerButton } from "@/components/dialer/FloatingDialerButton";
import {
  fetchPhoneCoverImages,
  fetchPhoneIconTypes,
  fetchPhoneQuickDials,
  type PhoneQuickDialDto,
} from "@/services/api";
import { useAuthStore, useFavouritesStore, useUiStore, type FavouriteContact } from "@/store";
const QUICK_STYLES: Array<{ bg: string; fg: string; Icon: LucideIcon }> = [
  { bg: "bg-papaya-deep/12", fg: "text-papaya-deep", Icon: Phone },
  { bg: "bg-papaya-leaf/15", fg: "text-papaya-leaf", Icon: Building2 },
  { bg: "bg-papaya/20", fg: "text-papaya-deep", Icon: Headphones },
  { bg: "bg-papaya-seed/10", fg: "text-papaya-seed dark:text-papaya", Icon: Shield },
  { bg: "bg-surface-muted", fg: "text-fg-muted", Icon: UserRound },
];

function greetingForNow(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function iconStyleFor(name?: string | null, index = 0) {
  const key = (name || "").toLowerCase();
  if (/recept|office|build|lobby/.test(key)) return QUICK_STYLES[1]!;
  if (/chief|person|user|agent/.test(key)) return QUICK_STYLES[0]!;
  if (/support|help|head/.test(key)) return QUICK_STYLES[2]!;
  if (/secur|guard|lock/.test(key)) return QUICK_STYLES[3]!;
  return QUICK_STYLES[index % QUICK_STYLES.length]!;
}

function statusCopy(regStatus: "offline" | "connecting" | "online") {
  if (regStatus === "online") {
    return { text: "Phone registered and ready", tone: "ok" as const };
  }
  if (regStatus === "connecting") {
    return { text: "Connecting phone…", tone: "warn" as const };
  }
  return {
    text: "Phone offline — check SIP credentials or open the dialer",
    tone: "err" as const,
  };
}

export function HomeWorkspace() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const regStatus = usePhoneStore((s) => s.regStatus);
  const makeCall = usePhoneStore((s) => s.makeCall);
  const openDialer = useUiStore((s) => s.openDialer);
  const setDialNumber = usePhoneStore((s) => s.setDialNumber);
  const favourites = useFavouritesStore((s) => s.items);
  const removeFavourite = useFavouritesStore((s) => s.removeFavourite);

  const quickDialQuery = useQuery({
    queryKey: queryKeys.phoneQuickDials,
    queryFn: fetchPhoneQuickDials,
  });
  const coverQuery = useQuery({
    queryKey: queryKeys.phoneCoverImages,
    queryFn: fetchPhoneCoverImages,
  });
  const iconTypesQuery = useQuery({
    queryKey: queryKeys.phoneIconTypes,
    queryFn: fetchPhoneIconTypes,
  });

  const iconNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const item of iconTypesQuery.data ?? []) {
      map.set(item.id, item.name);
    }
    return map;
  }, [iconTypesQuery.data]);

  const coverUrl = resolveMediaUrl(coverQuery.data?.[0]?.image ?? null);
  const extension =
    (profile?.webphone && "sip_username" in profile.webphone
      ? profile.webphone.sip_username
      : null) ||
    user?.username ||
    "";
  const displayName = user?.name || profile?.name || "Papaya";
  const orgName = profile?.organization?.name || user?.organizationName || null;
  const status = statusCopy(regStatus);
  const quickDials = quickDialQuery.data ?? [];

  function dialQuick(item: PhoneQuickDialDto) {
    const number = item.number?.trim();
    if (!number) return;
    setDialNumber(number);
    openDialer();
    if (regStatus === "online") {
      makeCall(number);
    }
  }

  function dialFavourite(item: FavouriteContact) {
    const number = item.number?.trim();
    if (!number) return;
    setDialNumber(number);
    openDialer();
    if (regStatus === "online") {
      makeCall(number);
    }
  }

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[6px] border border-border bg-surface shadow-[0_12px_36px_rgba(65,33,10,0.08)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.35)]">
      <header className="flex shrink-0 flex-wrap items-end justify-between gap-4 border-b border-border bg-surface px-5 py-4 sm:px-6">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm text-fg-muted">
            {greetingForNow()}
            <span
              className={cn(
                "inline-block h-2 w-2 rounded-full",
                status.tone === "ok" && "bg-papaya-leaf",
                status.tone === "warn" && "animate-pulse bg-warning",
                status.tone === "err" && "bg-danger",
              )}
              title={status.text}
            />
          </p>
          <h1 className="mt-0.5 truncate text-xl font-semibold tracking-tight text-fg sm:text-2xl">
            {displayName}
            {extension ? (
              <span className="font-semibold text-fg-muted"> ({extension})</span>
            ) : null}
          </h1>
          <p
            className={cn(
              "mt-1 max-w-2xl text-xs leading-relaxed sm:text-sm",
              status.tone === "ok" && "text-fg-muted",
              status.tone === "warn" && "text-warning",
              status.tone === "err" && "text-fg-subtle",
            )}
          >
            {status.text}
            {orgName ? ` · ${orgName}` : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(ROUTES.messages)}
            className="inline-flex h-9 items-center rounded-[6px] border border-border bg-surface px-3 text-sm font-medium text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg"
          >
            Messages
          </button>
          <button
            type="button"
            onClick={openDialer}
            className="inline-flex h-9 items-center gap-2 rounded-[6px] bg-papaya-deep px-3 text-sm font-semibold text-white transition-colors hover:bg-papaya-deep/90"
          >
            <PhoneCall className="h-4 w-4" />
            Dial
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <section className="border-b border-border">
          {coverQuery.isLoading ? (
            <div className="h-44 w-full animate-pulse bg-surface-muted sm:h-56 lg:h-64" />
          ) : coverUrl ? (
            <img
              src={coverUrl}
              alt="Organization cover"
              className="h-44 w-full object-cover sm:h-56 lg:h-64"
            />
          ) : (
            <div className="relative flex h-44 w-full items-center justify-center overflow-hidden bg-surface-muted sm:h-56 lg:h-64">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(251,170,51,0.28),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(114,176,85,0.22),transparent_40%)]" />
              <div className="relative flex flex-col items-center gap-2">
                <img
                  src="/logo.png"
                  alt=""
                  className="h-14 w-14 rounded-full bg-surface object-cover p-1 shadow-sm"
                />
                <p className="text-sm font-medium text-fg-muted">
                  {orgName || "Papaya"}
                </p>
              </div>
            </div>
          )}
        </section>

        <div className="grid w-full gap-0 lg:grid-cols-[1.4fr_1fr]">
          <section className="border-b border-border p-5 sm:p-6 lg:border-r lg:border-b-0">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-fg">Quick Access</h2>
                <p className="mt-0.5 text-xs text-fg-subtle">
                  One-tap dial from phonesetup
                </p>
              </div>
            </div>

            {quickDialQuery.isLoading ? (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-32 animate-pulse rounded-[6px] border border-border bg-surface-muted"
                  />
                ))}
              </div>
            ) : quickDialQuery.isError ? (
              <EmptyPanel message="Could not load quick dials from phonesetup." />
            ) : quickDials.length === 0 ? (
              <EmptyPanel message="No quick access contacts yet. Add them in phonesetup." />
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {quickDials.map((item, index) => {
                  const iconName = item.icon != null ? iconNameById.get(item.icon) : null;
                  const style = iconStyleFor(iconName || item.name, index);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => dialQuick(item)}
                      className="group flex flex-col items-center justify-center gap-3 rounded-[6px] border border-border bg-surface px-3 py-5 text-center transition-colors hover:border-papaya/40 hover:bg-surface-muted"
                    >
                      <span
                        className={cn(
                          "inline-flex h-12 w-12 items-center justify-center rounded-[6px] transition-transform group-hover:scale-[1.03]",
                          style.bg,
                          style.fg,
                        )}
                      >
                        <style.Icon className="h-5 w-5" strokeWidth={2.1} />
                      </span>
                      <span className="min-w-0 w-full">
                        <span className="block truncate text-sm font-semibold text-fg">
                          {item.name}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-fg-muted">
                          {item.number}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-fg">Favorites</h2>
                <p className="mt-0.5 text-xs text-fg-subtle">
                  Contacts you use most
                </p>
              </div>
              <button
                type="button"
                title="Add favorite"
                aria-label="Add favorite"
                onClick={() => navigate(ROUTES.calls)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-[6px] bg-papaya-deep text-white transition-colors hover:bg-papaya-deep/90"
              >
                <Plus className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>

            {favourites.length === 0 ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-[6px] border border-dashed border-border bg-surface-muted/60 px-6 py-10 text-center">
                <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-[6px] bg-papaya/15 text-papaya-deep">
                  <Star className="h-5 w-5" />
                </span>
                <p className="text-sm font-medium text-fg">No favorite contacts</p>
                <p className="mt-1 max-w-xs text-xs leading-relaxed text-fg-subtle">
                  Star a contact from call details to see them here
                </p>
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.calls)}
                  className="mt-4 inline-flex h-9 items-center rounded-[6px] border border-border bg-surface px-3 text-sm font-medium text-fg-muted transition-colors hover:border-papaya/40 hover:text-fg"
                >
                  Open calls
                </button>
              </div>
            ) : (
              <ul className="space-y-2">
                {favourites.map((item) => (
                  <li
                    key={item.key}
                    className="flex items-center gap-3 rounded-[6px] border border-border bg-surface px-3 py-2.5"
                  >
                    <button
                      type="button"
                      onClick={() => dialFavourite(item)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left transition-colors hover:opacity-90"
                    >
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-papaya/15 text-sm font-semibold text-papaya-deep">
                        {item.initials}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-fg">
                          {item.name}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-fg-muted">
                          {item.number}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      title="Remove favorite"
                      aria-label={`Remove ${item.name} from favorites`}
                      onClick={() => removeFavourite(item.number)}
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-papaya hover:bg-papaya/10"
                    >
                      <Star className="h-4 w-4 fill-papaya text-papaya" />
                    </button>
                    <button
                      type="button"
                      title={`Call ${item.name}`}
                      aria-label={`Call ${item.name}`}
                      onClick={() => dialFavourite(item)}
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-fg-muted hover:bg-surface-muted hover:text-fg"
                    >
                      <Phone className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
      <FloatingDialerButton />
    </div>
  );
}

function EmptyPanel({ message }: { message: string }) {
  return (
    <div className="rounded-[6px] border border-dashed border-border bg-surface-muted/50 px-4 py-10 text-center text-sm text-fg-muted">
      {message}
    </div>
  );
}
